<!--
 * Path: packages/frontend/src/features/browser/forms/pillButton/PillButton.vue
 * Task: A customizable pill button rendered in the Entity Header that maps to a specific relation.
 * Update: Dynamic variant assignment. Renders as 'text' for icons and 'tonal' (in default color) for text labels.
-->
<template>
  <!-- Edit Mode -->
  <v-menu v-if="activeEntity && canEdit" :close-on-content-click="false" location="bottom" offset="4" v-model="menuOpen">
    <template v-slot:activator="{ props }">
      <v-btn
        v-bind="props"
        :variant="isTextMode ? 'tonal' : 'text'"
        size="medium"
        :color="isTextMode ? undefined : (hasSelectedItems ? 'primary' : 'medium-emphasis')"
        class="bookmark-pill-btn transition-swing"
      >
        <div class="d-flex align-center h-100">
          <!-- Empty State: Just the Relation Icon -->
          <v-icon
            v-if="!hasSelectedItems"
            size="medium"
          >
            {{ relationDefinition?.icon || 'mdi-bookmark' }}
          </v-icon>

          <!-- Active State: Multiple Colored Icons or Labels -->
          <div v-else class="d-flex align-center px-1" style="gap: 4px;">
            <template v-for="(item, idx) in resolvedSelectedItems" :key="item.id">
              <!-- Show Icon if ShouldRenderAsIcon logic is true -->
              <v-icon
                v-if="item.shouldRenderAsIcon"
                :color="item.displayColor"
                size="medium"
              >
                {{ item.displayIcon }}
              </v-icon>
              <!-- Show Text Label if ShouldRenderAsIcon logic is false -->
              <span v-else class="text-caption font-weight-bold" :class="idx > 0 ? 'ms-1' : ''">
                    {{ item.title }}<span v-if="idx < resolvedSelectedItems.length - 1">,</span>
                 </span>
            </template>
          </div>
        </div>
        <v-tooltip activator="parent" location="bottom">{{ field.name }}</v-tooltip>
      </v-btn>
    </template>

    <!-- Menu Content: Multiselect or Single Select Dropdown -->
    <v-card min-width="300" class="rounded-xl elevation-4 border" style="overflow: visible;">
      <v-card-text class="pa-3">
        <div class="d-flex align-center mb-3">
          <v-icon :color="relationDefinition?.color || 'primary'" size="medium" class="me-2">
            {{ relationDefinition?.icon || 'mdi-link-variant' }}
          </v-icon>
          <span class="text-subtitle-2 font-weight-bold">Manage {{ field.name }}</span>
        </div>

        <EntitySelect
          v-model="selectedItems"
          :items="selectOptions"
          item-title="title"
          item-value="id"
          :label="field.name"
          :multiple="isMultiple"
          :chips="isMultiple"
          :closable-chips="isMultiple"
          class="mb-0"
        />

        <div class="d-flex justify-end mt-3">
          <v-btn variant="text" size="medium" color="medium-emphasis" @click="menuOpen = false" class="me-2">Close</v-btn>
          <v-btn variant="flat" size="smmediumall" color="primary" :loading="uiStore.loading.save" @click="saveChanges">Apply</v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>

  <!-- Read-Only Mode -->
  <div v-else-if="activeEntity && !canEdit && hasSelectedItems">
    <v-btn
      :variant="isTextMode ? 'tonal' : 'text'"
      disabled
      class="bookmark-pill-btn"
      :color="isTextMode ? undefined : 'primary'"
      style="opacity: 0.8;"
    >
      <div class="d-flex align-center px-1" style="gap: 4px;">
        <template v-for="(item, idx) in resolvedSelectedItems" :key="item.id">
          <!-- Show Icon -->
          <v-icon
            v-if="item.shouldRenderAsIcon"
            :color="item.displayColor"
            size="medium"
          >
            {{ item.displayIcon }}
          </v-icon>
          <!-- Show Text Label -->
          <span v-else class="text-caption font-weight-bold" :class="idx > 0 ? 'ms-1' : ''">
                {{ item.title }}<span v-if="idx < resolvedSelectedItems.length - 1">,</span>
             </span>
        </template>
      </div>
      <v-tooltip activator="parent" location="bottom">{{ field.name }}</v-tooltip>
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue';
import { useAuthStore } from '@/store/auth';
import { useWorkspaceStore } from '@/store/workspace';
import { useDatastoreStore } from '@/store/datastore';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import { useSelectOptions } from '@/core/composables/useSelectOptions';
import { FormContextKey } from '@/core/keys';
import EntitySelect from '@/core/components/common/EntitySelect.vue';
import type { Entity } from '@/core/types/editor';

interface FieldProps {
  id: string;
  name: string;
  entitySource?: string;
}

const props = defineProps<{ field: FieldProps }>();

const authStore = useAuthStore();
const workspaceStore = useWorkspaceStore();
const datastoreStore = useDatastoreStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const { getSelectOptions, entityInfoMap } = useSelectOptions();
const formContext = inject(FormContextKey, null);

const menuOpen = ref(false);

const activeEntity = computed<Entity | null>(() => {
  if (formContext) return formContext.entity.value;
  return workspaceStore.selectedEntity;
});

const relationDefinition = computed(() => {
  return authStore.appData?.relations?.find((r: any) => r.id === props.field.entitySource);
});

const isMultiple = computed(() => {
  return relationDefinition.value?.isMultiple ?? true;
});

const canEdit = computed(() => canEditWorkspace.value);

const hasSelectedItems = computed(() => {
  if (!activeEntity.value) return false;
  const rawVal = (activeEntity.value as any)[props.field.id];
  return Array.isArray(rawVal) ? rawVal.length > 0 : !!rawVal;
});

// Neu: Dynamisch berechnen, ob dieser Button primär Text anzeigt
const isTextMode = computed(() => {
  if (!hasSelectedItems.value) return false;
  return resolvedSelectedItems.value.some(item => !item.shouldRenderAsIcon);
});

// Local state for the menu dropdown
const selectedItems = ref<any[] | string | null>(isMultiple.value ? [] : null);

// Synchronize local state when menu opens
watch(menuOpen, (isOpen) => {
  if (isOpen && activeEntity.value) {
    const rawVal = (activeEntity.value as any)[props.field.id];
    if (isMultiple.value) {
      selectedItems.value = Array.isArray(rawVal) ? [...rawVal] : (rawVal ? [rawVal] : []);
    } else {
      selectedItems.value = Array.isArray(rawVal) ? rawVal[0] || null : rawVal || null;
    }
  }
});

const selectOptions = computed(() => {
  return getSelectOptions(props.field.entitySource || '', props.field.id) || [];
});

const resolvedSelectedItems = computed(() => {
  let ids: any[] = [];

  if (menuOpen.value) {
    ids = isMultiple.value ? (selectedItems.value as any[]) : (selectedItems.value ? [selectedItems.value] : []);
  } else {
    const rawVal = (activeEntity.value as any)?.[props.field.id];
    ids = Array.isArray(rawVal) ? rawVal : (rawVal ? [rawVal] : []);
  }

  if (ids.length === 0) return [];

  const options = selectOptions.value;
  return ids.map(id => {
    const opt = options.find((o: any) => o.id === id);
    if (opt) {
      // 1. Fetch the RAW entity from the datastore
      const rawEntity = datastoreStore.allEntities.find(e => e.id === id);

      // 2. Fetch the entity type meta information
      const typeInfo = opt.entityType ? entityInfoMap.value[opt.entityType] : null;

      // 3. Check if the entity type has a 'color' field assigned in its schema
      const entityDef = authStore.appData?.entities?.find((e: any) => e.name === opt.entityType);
      const hasColorFieldInSchema = !!entityDef?.fields?.some((f: any) => f.id === 'color');

      // 4. Render logic: Icon IF the instance has a color OR the schema supports color OR the type is a symbol
      const hasInstanceColor = !!(rawEntity && rawEntity.color);
      const isSymbolEntity = typeInfo && typeInfo.iconShape === 'none';
      const shouldRenderAsIcon = hasInstanceColor || hasColorFieldInSchema || isSymbolEntity;

      return {
        ...opt,
        shouldRenderAsIcon,
        displayColor: rawEntity?.color || opt.color || relationDefinition.value?.color || 'primary',
        displayIcon: rawEntity?.icon || opt.icon || relationDefinition.value?.icon || 'mdi-bookmark'
      };
    }
    return null;
  }).filter(Boolean);
});

const saveChanges = async () => {
  if (activeEntity.value) {
    // Process final value into an array or string based on relation definition
    let finalVal: any = null;

    if (isMultiple.value) {
      finalVal = Array.isArray(selectedItems.value) ? selectedItems.value : (selectedItems.value ? [selectedItems.value] : []);
    } else {
      finalVal = Array.isArray(selectedItems.value) ? selectedItems.value[0] || null : selectedItems.value || null;
    }

    (activeEntity.value as any)[props.field.id] = finalVal;
    await saveEntity(formContext?.isSecondary || false);
    menuOpen.value = false;
  }
};
</script>

<style scoped>
.bookmark-pill-btn {
  border-radius: 16px;
  height: 32px !important;
  min-width: 32px !important;
  padding: 0 8px !important;
}
/* Die alte .has-items Klasse mit der harten primären Hintergrundfarbe wurde komplett gelöscht */
</style>
